import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TransformWrapper, TransformComponent, useTransformContext } from 'react-zoom-pan-pinch';
import './HotspotMap.css';
import { apiFetch } from '../services/api';

/**
 * react-zoom-pan-pinch v3 blocks its wheel handler when isPanning === true
 * (see the library's isWheelAllowed gate). That prevents simultaneous
 * hold-click pan + scroll zoom. This component fills in the gap: when the
 * lib is panning, it does the wheel zoom math itself and writes directly
 * to the lib's transform state via setTransform.
 *
 * When the lib is NOT panning, this handler does nothing and the lib's
 * own (well-tested) wheel zoom takes over.
 */
function PanWheelZoomBridge({ containerRef, minScale = 1, maxScale = 4, step = 0.05 }) {
  const ctx = useTransformContext();
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      // Only override when the lib would have blocked it (active pan).
      if (!ctx.isPanning) return;
      const wrapper = ctx.wrapperComponent;
      if (!wrapper) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = wrapper.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const { scale, positionX, positionY } = ctx.state;
      const sign = e.deltaY > 0 ? -1 : 1;
      const factor = 1 + sign * step;
      const newScale = Math.max(minScale, Math.min(maxScale, scale * factor));
      if (newScale === scale) return;
      // Keep the cursor's underlying pixel fixed after scaling.
      const newPositionX = mouseX - ((mouseX - positionX) / scale) * newScale;
      const newPositionY = mouseY - ((mouseY - positionY) / scale) * newScale;
      // Write straight into the lib's transform state and re-render via
      // applyTransformation() — same path the lib uses internally.
      ctx.state.scale = newScale;
      ctx.state.positionX = newPositionX;
      ctx.state.positionY = newPositionY;
      if (typeof ctx.applyTransformation === 'function') {
        ctx.applyTransformation();
      } else if (ctx.contentComponent) {
        ctx.contentComponent.style.transform = `translate(${newPositionX}px, ${newPositionY}px) scale(${newScale})`;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => el.removeEventListener('wheel', onWheel, { capture: true });
  }, [containerRef, ctx, minScale, maxScale, step]);
  return null;
}

/**
 * SVG polygon hotspot overlay for the product-map image.
 *
 * Two modes:
 *   - VIEW (default): polygons are interactive — hover highlights, click navigates
 *     to .target (either an internal route via <Link> or an in-page anchor).
 *   - EDIT (?edit-hotspots=1 + admin token): adds a sidebar to pick a product,
 *     click on the image to add polygon points, drag points to move them, save
 *     to /api/storefront/settings.
 *
 * Hotspot data shape:
 *   [{ label, target, points: [[x%, y%], ...] }]
 */

// Default polygons — each product has TWO zones:
//   points       — covers the product itself in the image
//   labelPoints  — covers the white text-box label in the image
// Both polygons are clickable and navigate to the same target.
const DEFAULT_HOTSPOTS = [
  { label: 'B06 Premium Open-Arm Awning', target: '/products/b06-premium-open-arm-awning',          points: [[25,7],[45,7],[45,18],[25,18]],    labelPoints: [] },
  { label: 'AW01 Full Box Awning',         target: '/products/aw01-full-box-awning',                 points: [[47,10],[64,10],[64,22],[47,22]],  labelPoints: [] },
  { label: 'A09 Long-Projection Awning',   target: '/products/a09-long-projection-awning', points: [[64,16],[78,16],[78,28],[64,28]], labelPoints: [] },
  { label: 'K3 Heritage Mediterranean',    target: '/products/k3-heritage-mediterranean-awning',     points: [[80,30],[92,30],[92,43],[80,43]],  labelPoints: [] },
  { label: 'B05 Traditional Scalloped',    target: '/products/b05-traditional-scalloped-awning',     points: [[9,47],[23,47],[23,63],[9,63]],    labelPoints: [] },
  { label: 'Pergola 180 Retractable', target: '#pergolas-range', points: [[1,10],[22,10],[22,47],[1,47]],   labelPoints: [] },
  { label: 'Pergola 175',             target: '#pergolas-range', points: [[27,33],[52,33],[52,67],[27,67]], labelPoints: [] },
  { label: 'Pergola 163',             target: '#pergolas-range', points: [[18,64],[34,64],[34,88],[18,88]], labelPoints: [] },
];

const POINTS_API = '/api/storefront/settings/home_rangemap_hotspots';

function pointsToString(points) {
  return points.map(([x, y]) => `${x},${y}`).join(' ');
}

export default function HotspotMap({ src, alt }) {
  const [hotspots, setHotspots] = useState(DEFAULT_HOTSPOTS);
  const [loaded, setLoaded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [activeZone, setActiveZone] = useState('points'); // 'points' (product) | 'labelPoints' (text label)
  const [hoverIdx, setHoverIdx] = useState(-1);
  const [draggingPt, setDraggingPt] = useState(null); // {hotspot, zone, point}
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  });
  // Ref to the outer pan/zoom container — used by PanWheelZoomBridge
  // to attach a wheel listener that does the zoom math itself while the
  // library is in panning mode. Mobile only; desktop renders a plain image.
  const inlineContainerRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const imgRef = useRef(null);
  const navigate = useNavigate();

  // Track desktop vs touch device so we can render a plain (no
  // pan/zoom) image on desktop while keeping the existing pan/zoom UX on
  // mobile/touch where it's actually useful.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Detect edit mode. SEC-06 Phase 5: the previous code gated edit
  // mode on a localStorage 'adminToken' read, which no longer exists
  // (auth lives in an HttpOnly cookie now and JS can't see it). The
  // save endpoint validates admin auth server-side via the cookie,
  // so we don't lose anything by showing the edit UI to anyone with
  // ?edit-hotspots=1 in the URL — they just can't persist changes
  // without being signed in as admin.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit-hotspots') === '1') {
      setEditMode(true);
      setAuthed(true);
    }
  }, []);

  // Fetch saved hotspots
  useEffect(() => {
    let cancelled = false;
    apiFetch(POINTS_API)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data && Array.isArray(data.value) && data.value.length > 0) {
          // Migrate older records that don't have labelPoints
          const migrated = data.value.map((h) => ({
            ...h,
            points: Array.isArray(h.points) ? h.points : [],
            labelPoints: Array.isArray(h.labelPoints) ? h.labelPoints : [],
          }));
          setHotspots(migrated);
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => { cancelled = true; };
  }, []);

  // Translate a click event to image-relative percentage coordinates
  const eventToPercent = (e) => {
    const img = imgRef.current;
    if (!img) return [0, 0];
    const r = img.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    return [Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y))];
  };

  // EDIT — click to add a point to the currently active hotspot+zone
  const onSvgClick = useCallback((e) => {
    if (!editMode) return;
    if (draggingPt) return; // drag end handles this
    const [x, y] = eventToPercent(e);
    setHotspots((prev) => prev.map((h, i) => {
      if (i !== activeIdx) return h;
      const cur = h[activeZone] || [];
      return { ...h, [activeZone]: [...cur, [Math.round(x * 10) / 10, Math.round(y * 10) / 10]] };
    }));
  }, [editMode, activeIdx, activeZone, draggingPt]);

  // EDIT — start dragging a specific point in a specific zone
  const onPointMouseDown = (hotspotIdx, zoneKey, ptIdx) => (e) => {
    if (!editMode) return;
    e.stopPropagation();
    setDraggingPt({ h: hotspotIdx, z: zoneKey, p: ptIdx });
  };
  // EDIT — global mousemove updates dragged point
  useEffect(() => {
    if (!editMode || !draggingPt) return;
    const onMove = (e) => {
      const [x, y] = eventToPercent(e);
      setHotspots((prev) => prev.map((h, i) => {
        if (i !== draggingPt.h) return h;
        const cur = h[draggingPt.z] || [];
        const nextPts = cur.map((p, j) => (j === draggingPt.p
          ? [Math.round(x * 10) / 10, Math.round(y * 10) / 10] : p));
        return { ...h, [draggingPt.z]: nextPts };
      }));
    };
    const onUp = () => setDraggingPt(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [editMode, draggingPt]);

  // EDIT — actions
  const undoLastPoint = () => {
    setHotspots((prev) => prev.map((h, i) => {
      if (i !== activeIdx) return h;
      const cur = h[activeZone] || [];
      return { ...h, [activeZone]: cur.slice(0, -1) };
    }));
  };
  const clearActive = () => {
    const zoneName = activeZone === 'points' ? 'product zone' : 'label zone';
    if (!window.confirm(`Clear the ${zoneName} for this product?`)) return;
    setHotspots((prev) => prev.map((h, i) => (i === activeIdx ? { ...h, [activeZone]: [] } : h)));
  };
  const removePoint = (hotspotIdx, zoneKey, ptIdx) => () => {
    if (!editMode) return;
    setHotspots((prev) => prev.map((h, i) => {
      if (i !== hotspotIdx) return h;
      const cur = h[zoneKey] || [];
      return { ...h, [zoneKey]: cur.filter((_, j) => j !== ptIdx) };
    }));
  };
  const saveHotspots = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await apiFetch(POINTS_API, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: hotspots }),
      });
      if (res.ok) setSaveMsg('Saved.');
      else setSaveMsg('Save failed - check admin login.');
    } catch (e) {
      setSaveMsg('Save failed - network error.');
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(''), 2500);
  };

  // VIEW — click polygon → navigate
  const onPolygonClick = (h) => () => {
    if (editMode) return;
    if (h.target.startsWith('#')) {
      const el = document.querySelector(h.target);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(h.target);
    }
  };

  // The image + SVG hotspots live inside one transformable subtree so both
  // scale/pan together. Edit mode bypasses the wrapper because the
  // point-dragging interactions need raw mouse coordinates.
  const imageAndHotspots = (
    <>
      <img
        ref={imgRef}
        src={src}
        alt={alt || ''}
        loading="lazy"
        width={1920}
        height={1080}
        className="hsm-image"
        draggable={false}
      />
      {loaded && (
        <svg
          className="hsm-svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          onClick={onSvgClick}
        >
          {renderHotspotPolys()}
          {/* edit-mode point handles — active polygon's active zone only */}
          {editMode && hotspots[activeIdx] && (hotspots[activeIdx][activeZone] || []).map((p, j) => (
            <circle
              key={`${activeIdx}-${activeZone}-${j}`}
              cx={p[0]}
              cy={p[1]}
              r={0.8}
              className="hsm-pt hsm-pt--active"
              onMouseDown={onPointMouseDown(activeIdx, activeZone, j)}
              onContextMenu={(e) => { e.preventDefault(); removePoint(activeIdx, activeZone, j)(); }}
            />
          ))}
        </svg>
      )}
    </>
  );
  // Extracted hotspot polys so we can render them inside the SVG above.
  function renderHotspotPolys() {
    return hotspots.map((h, i) => {
      const zones = [
        { key: 'points', pts: h.points || [] },
        { key: 'labelPoints', pts: h.labelPoints || [] },
      ];
      return zones.map(({ key, pts }) => {
        if (pts.length < 3) return null;
        const isActiveProduct = editMode && i === activeIdx && key === activeZone;
        const isHover = !editMode && hoverIdx === i;
        return (
          <polygon
            key={`${i}-${key}`}
            points={pointsToString(pts)}
            data-target={h.target}
            className={`hsm-poly ${isActiveProduct ? 'hsm-poly--active' : ''} ${isHover ? 'hsm-poly--hover' : ''}`}
            onMouseEnter={() => !editMode && setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(-1)}
            onClick={(e) => {
              if (editMode) {
                e.stopPropagation();
                setActiveIdx(i);
                setActiveZone(key);
                return;
              }
              onPolygonClick(h)();
            }}
          />
        );
      });
    });
  }

  return (
    <div className={`hsm-wrap ${editMode ? 'hsm-wrap--edit' : ''}`}>
      <div className="hsm-image-wrap">
        {editMode || isDesktop ? (
          imageAndHotspots
        ) : (
          <div ref={inlineContainerRef} className="hsm-zoom-container">
            <TransformWrapper
              initialScale={1}
              minScale={1}
              maxScale={4}
              limitToBounds={true}
              centerOnInit={true}
              doubleClick={{ mode: 'zoomIn', step: 0.7 }}
              wheel={{ step: 0.005 }}
              centerZoomedOut={true}
              pinch={{ step: 5 }}
              panning={{ velocityDisabled: false }}
            >
              <PanWheelZoomBridge containerRef={inlineContainerRef} minScale={1} maxScale={4} step={0.05} />
              <TransformComponent
                wrapperClass="hsm-zoom-wrap"
                contentClass="hsm-zoom-content"
              >
                {imageAndHotspots}
              </TransformComponent>
            </TransformWrapper>
          </div>
        )}
        {/* Active-product banner (edit mode) */}
        {editMode && authed && hotspots[activeIdx] && (
          <div className="hsm-active-banner">
            {hotspots[activeIdx].label}
            <small>
              Zone: <strong>{activeZone === 'points' ? 'Product' : 'Label'}</strong>
              {' · '}
              {(hotspots[activeIdx][activeZone] || []).length} point
              {(hotspots[activeIdx][activeZone] || []).length === 1 ? '' : 's'}
              {' · '}Click image to add
            </small>
          </div>
        )}

        {/* Hover label (view mode) */}
        {!editMode && hoverIdx >= 0 && hotspots[hoverIdx] && (
          <div
            className="hsm-tooltip"
            style={(() => {
              const pts = hotspots[hoverIdx].points;
              const cx = pts.reduce((s, [x]) => s + x, 0) / pts.length;
              const cy = pts.reduce((s, [, y]) => s + y, 0) / pts.length;
              return { left: `${cx}%`, top: `${cy}%` };
            })()}
          >
            {hotspots[hoverIdx].label}
          </div>
        )}
      </div>

      {/* EDITOR PANEL */}
      {editMode && authed && (
        <div className="hsm-editor">
          <div className="hsm-editor-head">
            <strong>Hotspot Editor</strong>
            <span className="hsm-editor-hint">
              Click image to add points · Drag points to move · Right-click point to delete
            </span>
          </div>
          <div className="hsm-editor-list">
            {hotspots.map((h, i) => {
              const pCount = (h.points || []).length;
              const lCount = (h.labelPoints || []).length;
              return (
                <button
                  key={i}
                  type="button"
                  className={`hsm-editor-item ${i === activeIdx ? 'hsm-editor-item--active' : ''}`}
                  onClick={() => setActiveIdx(i)}
                >
                  <span>{h.label}</span>
                  <span className="hsm-editor-count">P:{pCount} · L:{lCount}</span>
                </button>
              );
            })}
          </div>

          {/* Zone toggle for the active product */}
          <div className="hsm-zone-toggle">
            <span>Drawing zone:</span>
            <button
              type="button"
              className={`hsm-zone-btn ${activeZone === 'points' ? 'hsm-zone-btn--on' : ''}`}
              onClick={() => setActiveZone('points')}
            >Product</button>
            <button
              type="button"
              className={`hsm-zone-btn ${activeZone === 'labelPoints' ? 'hsm-zone-btn--on' : ''}`}
              onClick={() => setActiveZone('labelPoints')}
            >Label</button>
          </div>

          <div className="hsm-editor-actions">
            <button type="button" className="hsm-btn-ghost" onClick={undoLastPoint}>Undo last point</button>
            <button type="button" className="hsm-btn-ghost" onClick={clearActive}>Clear active zone</button>
            <button type="button" className="hsm-btn-primary" onClick={saveHotspots} disabled={saving}>
              {saving ? 'Saving…' : 'Save all'}
            </button>
            {saveMsg && <span className="hsm-editor-msg">{saveMsg}</span>}
          </div>
        </div>
      )}
      {editMode && !authed && (
        <div className="hsm-editor hsm-editor--locked">
          {/* No link to the admin login here - it must not be linked from any
              public surface. This block only appears with ?edit-hotspots=1,
              but that URL is public, so the instruction stays text-only. */}
          Editor mode is admin-only. Sign in to the admin area first, then come
          back to <code>?edit-hotspots=1</code>.
        </div>
      )}
    </div>
  );
}
